import React from 'react';

import { makeStyles } from 'tss-react/mui';
import { FilterList } from '@mui/icons-material';
import { Button, ButtonProps, ClosableMenu } from '@uplift/ui-v2';

import { PracticeStateRow } from '../PracticeStatesTable';
import { PracticeStatesStatusFilterValue } from './PracticeStatesStatusFilter.types';
import { PracticeStatesStatusFilterItem } from './PracticeStatesStatusFilterItem.component';

const useStyles = makeStyles()(theme => ({
  button: {},
  menuPaper: {
    marginTop: theme.spacing(0.5),
  },
}));

export type PracticeStatesStatusFilterProps = {
  rows: PracticeStateRow[];
  value: PracticeStatesStatusFilterValue;
  setValue: (v: PracticeStatesStatusFilterValue) => void;
  classes?: Partial<ReturnType<typeof useStyles>['classes']>;
  buttonSize?: ButtonProps['size'];
  disabled?: boolean;
};

export const PracticeStatesStatusFilter: React.FC<PracticeStatesStatusFilterProps> = ({ value, setValue, rows, classes: _classes, buttonSize, disabled }) => {
  const { classes } = useStyles(undefined, { props: { classes: _classes } });
  const [ anchorEl, setAnchorEl ] = React.useState<HTMLElement | null>(null);

  const counts = React.useMemo(() => ({
    live: rows.filter(r => r.status === 'live').length,
    inReview: rows.filter(r => r.status === 'inReview').length,
    getStarted: rows.filter(r => r.status === 'getStarted').length,
    expired: rows.filter(r => r.status === 'expired').length,
    all: rows.length,
  }), [ rows ]);

  const options = React.useMemo(() => [
    { value: 'live' as const, label: `Live (${counts.live})` },
    { value: 'inReview' as const, label: `In Review (${counts.inReview})` },
    { value: 'getStarted' as const, label: `Not Started (${counts.getStarted})` },
    { value: 'expired' as const, label: `Expired (${counts.expired})` },
    { value: 'all' as const, label: `All (${counts.all})` },
  ], [ counts ]);

  const currentOption = React.useMemo(() => options.find(o => o.value === value), [ options, value ]);

  return (
    <>
      <Button
        className={classes.button}
        startIcon={<FilterList />}
        variant="outlined"
        color="secondary"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        size={buttonSize}
        disabled={disabled}
      >
        {currentOption?.label}
      </Button>
      <ClosableMenu
        PaperProps={{ className: classes.menuPaper }}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {options.map(o => (
          <PracticeStatesStatusFilterItem
            key={o.value}
            label={o.label}
            value={o.value}
            selectedValue={value}
            setValue={v => {
              setValue(v);
              setAnchorEl(null);
            }}
          />
        ))}
      </ClosableMenu>
    </>
  );
};
